/* eslint-disable @typescript-eslint/no-unused-vars */

import { CellValueChangedEvent, GridOptions, GridReadyEvent, RowSelectedEvent } from '@ag-grid-community/core';
import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { AgFns, GridOptionsExt, ISortModel, ProxAgFns, UtilDialogService, wrapBusy } from '@core';
import {
  AccountIssuance,
  AccountIssuanceUserGroupMap,
  KitTag,
  Program,
  ProgramAccountIssuanceMap,
  ProgramIssuance,
  ProgramIssuanceUserGroupMap,
  ProgramKit,
  ProgramKitIssuance,
  ProgramKitIssuanceUserGroupMap,
  PricedProductType,
  ProgramProductTypeTag,
  ProgramUserGroup,
  ProgramViolationRule,
} from '@models';
import { DialogService } from 'primeng/dynamicdialog';
import { ProgramFrmComponent } from './program-frm.component';
import { AccountDbQueryService } from '../services/account-db-query.service';
import { AccountDbSaveService } from '../services/account-db-save.service';
import { EntityFns } from '@data';
import * as _ from 'lodash';
import { UtilFns } from '@utils';
import { ToastrService } from 'ngx-toastr';
import { Entity } from 'breeze-client';

@Component({
  selector: 'prox-program-issuances-sub',
  templateUrl: './program-issuances.sub.component.html',
  standalone: false,
  encapsulation: ViewEncapsulation.None,
  styles: [
    `
      prox-program-issuances-sub {
        .p-tabpanels {
          padding: 0 !important;
        }
      }

      prox-program-issuances-sub {
        prox-field label {
          margin-left: 5px;
          font-weight: 600;
          font-size: 1rem;
        }
      }
    `,
  ],
})
export class ProgramIssuancesSubComponent implements OnInit, OnChanges {
  @Input() parent!: ProgramFrmComponent;
  @Input() visible!: boolean;

  piGridOptions: GridOptionsExt = {};
  programIssuances: ProgramIssuance[] = [];
  selectedPi?: ProgramIssuance;

  piTagProductGridOptions: GridOptionsExt = {};
  piTagProducts: PricedProductType[] = [];

  kiGridOptions: GridOptionsExt = {};
  kitIssuances: ProgramKitIssuance[] = [];
  selectedKi?: ProgramKitIssuance;

  aiGridOptions: GridOptionsExt = {};
  accountIssuances: AccountIssuance[] = [];
  selectedAi?: AccountIssuance;

  aiTagProductGridOptions: GridOptionsExt = {};
  aiTagProducts: PricedProductType[] = [];

  availablePiPugs: ProgramUserGroup[] = [];
  piPugGridOptions: GridOptionsExt = {};

  availableKiPugs: ProgramUserGroup[] = [];
  kiPugGridOptions: GridOptionsExt = {};

  availableAiPugs: ProgramUserGroup[] = [];
  aiPugGridOptions: GridOptionsExt = {};

  programViolationRules!: ProgramViolationRule[];
  programProductTypeTags: ProgramProductTypeTag[] = [];
  kitTags: KitTag[] = [];

  tabViewIndex = 0;

  constructor(
    public dbQueryService: AccountDbQueryService,
    public dbSaveService: AccountDbSaveService,
    public dialogService: UtilDialogService,
    public pngDialogService: DialogService,
    public toastr: ToastrService,
  ) {
    //
  }

  get program() {
    return this.parent.program;
  }

  async ngOnInit() {
    this.programProductTypeTags = this.program.account.programProductTypeTags.slice().sort((a, b) => a.name.localeCompare(b.name));
    this.programProductTypeTags.unshift({
      id: UtilFns.EmptyGuid,
      name: '- None -',
    } as ProgramProductTypeTag);

    this.kitTags = this.parent.allKitTags;

    this.piGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPiGridReady,
      onRowSelected: this.onPiRowSelected,
      onCellValueChanged: this.onPiCellValueChanged,

      rowModelType: 'clientSide',
      // params.data is ProgramIssueance
      getRowId: (params) => params.data.id,
    });

    this.piTagProductGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPiTagProductGridReady,
      rowModelType: 'clientSide',
    });

    this.kiGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onKiGridReady,
      onRowSelected: this.onKiRowSelected,
      onCellValueChanged: this.onKiCellValueChanged,

      rowModelType: 'clientSide',
      // params.data is ProgramIssueance
      getRowId: (params) => params.data.id,
    });

    this.aiGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onAiGridReady,
      onRowSelected: this.onAiRowSelected,
      onCellValueChanged: this.onAiCellValueChanged,

      rowModelType: 'clientSide',
      // params.data is AccountIssueance
      getRowId: (params) => params.data.id,
    });

    this.aiTagProductGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onAiTagProductGridReady,
      rowModelType: 'clientSide',
    });

    this.piPugGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPiPugGridReady,
      onCellValueChanged: this.onPiPugCellValueChanged,

      rowModelType: 'clientSide',
      // params.data is ProgramUserGroup
      getRowId: (params) => params.data.id,
    });

    this.kiPugGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onKiPugGridReady,
      onCellValueChanged: this.onKiPugCellValueChanged,

      rowModelType: 'clientSide',
      // params.data is ProgramUserGroup
      getRowId: (params) => params.data.id,
    });

    this.aiPugGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onAiPugGridReady,
      onCellValueChanged: this.onAiAugCellValueChanged,

      rowModelType: 'clientSide',
      // params.data is ProgramUserGroup
      getRowId: (params) => params.data.id,
      getRowStyle: (params): any => {
        const pug = params.data as ProgramUserGroup;
        if (!pug || !this.program.approvalTree) return undefined;
        const isThisProgram = this.program.approvalTree.approvalTreeUserGroups.some((x) => x.programUserGroupId === pug.id);
        return isThisProgram ? { fontWeight: '600' } : { fontStyle: 'italic', opacity: '0.7' };
      },
    });
    this.refresh();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.refresh();
  }

  @wrapBusy
  async refresh(forceRefresh = false) {
    if (!this.visible && !forceRefresh) return;
    this.parent.updateComponentNameMap(this);

    this.programViolationRules = await this.dbQueryService.getAll(ProgramViolationRule);

    const isLoaded = this.program.entityAspect.isNavigationPropertyLoaded('programIssuances');
    if (isLoaded && !forceRefresh) {
      this.programIssuances = this.program.programIssuances;
    } else {
      this.programIssuances = await this.dbQueryService.getProgramIssuances(this.program.id);
      this.kitIssuances = await this.dbQueryService.getProgramKitIssuances(this.program.id);
      const allAccountIssuances = await this.dbQueryService.getAccountIssuances(this.program.accountId);
      // Show only Account Issuances whose tag is actually referenced by a product in this
      // program. The full account-wide list is irrelevant noise on a per-program editor.
      // Issuances already mapped to this program are always kept, even if no current product
      // carries the tag (e.g., the last product was removed but the include is still active).
      this.accountIssuances = this.filterAccountIssuancesForProgram(allAccountIssuances);

      this.program.entityAspect.markNavigationPropertyAsLoaded('programIssuances');
      this.program.entityAspect.markNavigationPropertyAsLoaded('programKitIssuances');
      this.program.account.entityAspect.markNavigationPropertyAsLoaded('accountIssuances');

      // can happen if copying a program
      if (this.selectedPi?.programId != this.program.id) {
        this.selectedPi = undefined;
      }
    }

    // needed to refresh product type tag dropdowns and editability
    this.onPiGridReady();
    await AgFns.refreshGrid(this.piGridOptions, this.programIssuances);
    this.onKiGridReady();
    await AgFns.refreshGrid(this.kiGridOptions, this.kitIssuances);
    this.onAiGridReady();
    await AgFns.refreshGrid(this.aiGridOptions, this.accountIssuances);

    this.onPiPugGridReady();
    this.onAiPugGridReady();
    this.onKiPugGridReady();

    this.refreshAvailablePiPugs(this.selectedPi);
    AgFns.sizeColumnsToFit(this.piGridOptions);
    AgFns.selectDefaultRow(this.piGridOptions);

    AgFns.selectDefaultRow(this.kiGridOptions);
    AgFns.selectDefaultRow(this.aiGridOptions);

    AgFns.sizeColumnsToFit(this.kiGridOptions);
    AgFns.sizeColumnsToFit(this.kiPugGridOptions);

    AgFns.sizeColumnsToFit(this.aiGridOptions);
    AgFns.sizeColumnsToFit(this.aiPugGridOptions);
  }

  onTabChange(x: any) {
    //
  }

  onPiGridReady(event?: GridReadyEvent) {
    const colDefs = [
      ProxAgFns.getEntityStateColDef(),
      {
        headerName: 'Program Product Tag',
        editable: !this.parent.isReadOnly(),

        filter: 'agTextColumnFilter',
        ...AgFns.createDropdownEditor('programProductTypeTagId', this.programProductTypeTags),
        colId: 'productTypeTag',
        ...AgFns.flexWidth(100),
      },
      {
        headerName: 'Name',
        field: 'name',
        editable: !this.parent.isReadOnly(),
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(100),
      },
      {
        headerName: 'Min Qty',
        field: 'minimumQty',
        editable: !this.parent.isReadOnly(),
        ...AgFns.fixWidth(120),
      },
      {
        headerName: 'Max Qty',
        field: 'maximumQty',
        editable: !this.parent.isReadOnly(),
        ...AgFns.fixWidth(120),
      },
      this.parent.ifEditable(ProxAgFns.getEntityDeleteColDef(this.onPiDelete.bind(this))),
    ];
    const sortModel: ISortModel = [{ colId: 'name', sort: 'asc' }];
    AgFns.initGrid(event ?? this.piGridOptions, colDefs, sortModel);
  }

  onPiTagProductGridReady(event?: GridReadyEvent) {
    const ptypePath = 'productType';
    const colDefs = [
      { ...ProxAgFns.getSupplierColDef(ptypePath + '.supplier.name') },
      { ...ProxAgFns.getManufacturerColDef(ptypePath + '.manufacturer.name') },
      { ...ProxAgFns.getProductTypeNameColDef(ptypePath + '.name') },
    ];
    const sortModel: ISortModel = [
      { colId: ptypePath + '.supplier.name', sort: 'asc' },
      { colId: ptypePath + '.manufacturer.name', sort: 'asc' },
      { colId: ptypePath + '.name', sort: 'asc' },
    ];
    AgFns.initGrid(event ?? this.piTagProductGridOptions, colDefs, sortModel);
  }

  onPiPugGridReady(event?: GridReadyEvent) {
    const colDefs = [
      {
        headerName: 'Applies to Group',
        field: 'name',
        cellRenderer: 'agGroupCellRenderer',
        ...AgFns.flexWidth(100),
      },
      {
        ...AgFns.createIncludedInListCheckBoxProps(
          ProgramIssuanceUserGroupMap,
          this.dbQueryService.uow,
          'Is Included?',
          (e) => {
            return this.selectedPi?.programIssuanceUserGroupMaps.find((x) => x.programUserGroupId == e.data.id) != null;
          },
          (e) => {
            return {
              programIssuanceId: this.selectedPi?.id,
              programUserGroupId: e.data.id,
            };
          },
          !this.parent.isReadOnly(),
        ),
        ...AgFns.fixWidth(120),
      },
    ];
    const sortModel: ISortModel = [{ colId: 'name', sort: 'asc' }];
    AgFns.initGrid(event ?? this.piPugGridOptions, colDefs, sortModel);
  }

  @wrapBusy
  async onPiRowSelected(event: RowSelectedEvent) {
    if (!event.node.isSelected()) return;
    const pi = event.data as ProgramIssuance;
    if (pi == null) return;
    this.selectedPi = pi;
    if (pi.entityAspect.entityState.isDeleted()) {
      this.selectedPi = undefined;
    }

    await this.refreshAvailablePiPugs(this.selectedPi);
    await this.refreshPiTagProducts();
  }

  @wrapBusy
  async onPiAdd() {
    if (!this.programIssuances) return;
    if (this.program.approvalTree == null) {
      await this.dialogService.okDialog('Unable to add', 'A Proximity Organization must be specified before you can add an issuance limit');
      return;
    }

    const pi = this.dbSaveService.createEntity(ProgramIssuance, {
      programId: this.program.id,
    });
    pi.entityAspect.validateEntity();

    this.programIssuances?.push(pi);
    AgFns.refreshGrid(this.piGridOptions, this.programIssuances);
    AgFns.selectGridRowByKey(this.piGridOptions, (e: ProgramIssuance) => e.id, pi.id, 'name');
  }

  @wrapBusy
  async onPiDelete(pi: ProgramIssuance) {
    if (!this.programIssuances) return;

    UtilFns.assertNonNull(pi, 'Product Group should have been found');
    const ok = await this.dialogService.askIfOk(
      'Confirm Delete',
      `You will delete this Program Issuance record along with all job order logs associated with it.  THIS IS NOT REVERSIBLE.  Are you sure?`,
    );
    if (!ok) return;
    const logs = await this.dbQueryService.getProgramIssuanceUserLogsByProgramIssuanceId(pi.id);
    logs.forEach((x) => EntityFns.deleteOrDetach(x.entityAspect));
    pi.programIssuanceUserGroupMaps.slice().forEach((x) => EntityFns.deleteOrDetach(x.entityAspect));
    EntityFns.deleteOrDetach(pi.entityAspect);

    // for display
    _.remove(this.programIssuances, pi);
    AgFns.refreshGrid(this.piGridOptions, this.programIssuances);
    await this.refreshAvailablePiPugs(undefined);
    //TODO: select 'next' row row
  }

  onPiCellValueChanged(event: CellValueChangedEvent) {
    if (this.selectedPi == null) return;
    if (event.colDef.colId == 'productTypeTag') {
      this.selectedPi.programIssuanceUserGroupMaps.slice().forEach((x) => EntityFns.deleteOrDetach(x.entityAspect));
      this.refreshAvailablePiPugs(this.selectedPi);
      this.refreshPiTagProducts();
      if ((this.selectedPi.name ?? '').length == 0) {
        this.selectedPi.name = this.selectedPi.programProductTypeTag.name;
        this.piGridOptions.apiX?.refreshCells();
      }
    }
  }

  onPiPugCellValueChanged(event: CellValueChangedEvent) {
    if (this.selectedPi?.programIssuanceUserGroupMaps.length == 0) {
      this.toastr.warning('At least one Account User Group must be selected');
    }
  }

  onPiSelectAllGroups() {
    if (!this.selectedPi) return;
    for (const pug of this.availablePiPugs) {
      const exists = this.selectedPi.programIssuanceUserGroupMaps.find((x) => x.programUserGroupId === pug.id);
      if (!exists) {
        // Try to restore a deleted entity first, otherwise create a new one
        const restored = this.dbQueryService.uow.undoIfDeleted(ProgramIssuanceUserGroupMap, [this.selectedPi.id, pug.id]);
        if (!restored) {
          this.dbSaveService.createEntity(ProgramIssuanceUserGroupMap, {
            programIssuanceId: this.selectedPi.id,
            programUserGroupId: pug.id,
          });
        }
      }
    }
    this.piPugGridOptions.apiX?.refreshCells();
  }

  onPiDeselectAllGroups() {
    if (!this.selectedPi) return;
    this.selectedPi.programIssuanceUserGroupMaps.slice().forEach((x) => EntityFns.deleteOrDetach(x.entityAspect));
    this.piPugGridOptions.apiX?.refreshCells();
  }

  async refreshAvailablePiPugs(iss?: ProgramIssuance) {
    if (iss == null || iss.programProductTypeTag == null) {
      this.availablePiPugs = [];
    } else {
      const usedPugIds = new Set(
        _.flatten(
          this.program.programIssuances
            // all issuances with same tag as this one
            .filter((x) => x.programProductTypeTagId == iss.programProductTypeTagId)
            .map((x) => x.programIssuanceUserGroupMaps.map((x) => x.programUserGroupId)),
        ),
      );

      const pugs = this.program.approvalTree.approvalTreeUserGroups.map((x) => x.programUserGroup).filter((x) => x != null);
      const availablePugs = pugs.filter((x) => !usedPugIds.has(x.id));

      const currentPugs = iss.programIssuanceUserGroupMaps.map((x) => x.programUserGroup).filter((x) => x != null);
      availablePugs.push(...currentPugs);
      this.availablePiPugs = availablePugs;
    }
    await AgFns.refreshGrid(this.piPugGridOptions, this.availablePiPugs, 'sizeToFit');
    return this.availablePiPugs;
  }

  async refreshPiTagProducts() {
    if (this.selectedPi == null || this.selectedPi.programProductTypeTag == null) {
      this.piTagProducts = [];
    } else {
      this.piTagProducts = await this.dbQueryService.getPricedProductTypesForTag(this.selectedPi.programProductTypeTagId);
    }
    await AgFns.refreshGrid(this.piTagProductGridOptions, this.piTagProducts, 'sizeToFit');
  }

  // --------------------------------------------------

  onKiGridReady(event?: GridReadyEvent) {
    const colDefs = [
      ProxAgFns.getEntityStateColDef(),
      {
        headerName: 'Kit Tag',
        editable: !this.parent.isReadOnly(),

        filter: 'agTextColumnFilter',
        ...AgFns.createDropdownEditor('kitTagId', this.kitTags),
        colId: 'productTypeTag',
        ...AgFns.flexWidth(100),
      },
      {
        headerName: 'Name',
        field: 'name',
        editable: !this.parent.isReadOnly(),
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(100),
      },
      {
        headerName: 'Min Qty',
        field: 'minimumQty',
        editable: !this.parent.isReadOnly(),
        ...AgFns.fixWidth(120),
      },
      {
        headerName: 'Max Qty',
        field: 'maximumQty',
        editable: !this.parent.isReadOnly(),
        ...AgFns.fixWidth(120),
      },
      this.parent.ifEditable(ProxAgFns.getEntityDeleteColDef(this.onKiDelete.bind(this))),
    ];
    const sortModel: ISortModel = [{ colId: 'name', sort: 'asc' }];
    AgFns.initGrid(event ?? this.kiGridOptions, colDefs, sortModel);
  }

  onKiCellValueChanged(event: CellValueChangedEvent) {
    if (this.selectedKi == null) return;
    if (event.colDef.colId == 'KitTag') {
      this.selectedKi.programKitIssuanceUserGroupMaps.slice().forEach((x) => EntityFns.deleteOrDetach(x.entityAspect));
      this.refreshAvailableKiPugs(this.selectedKi);
      if ((this.selectedKi.name ?? '').length == 0) {
        this.selectedKi.name = this.selectedKi.kitTag.name;
        this.kiGridOptions.apiX?.refreshCells();
      }
    }
  }

  onKiPugGridReady(event?: GridReadyEvent) {
    const colDefs = [
      {
        headerName: 'Applies to Group',
        field: 'name',
        cellRenderer: 'agGroupCellRenderer',
        ...AgFns.flexWidth(100),
      },
      {
        ...AgFns.createIncludedInListCheckBoxProps(
          ProgramKitIssuanceUserGroupMap,
          this.dbQueryService.uow,
          'Is Included?',
          (e) => {
            return this.selectedKi?.programKitIssuanceUserGroupMaps.find((x) => x.programUserGroupId == e.data.id) != null;
          },
          (e) => {
            return {
              programKitIssuanceId: this.selectedKi?.id,
              programUserGroupId: e.data.id,
            };
          },
          !this.parent.isReadOnly(),
        ),
        ...AgFns.fixWidth(120),
      },
    ];
    const sortModel: ISortModel = [{ colId: 'name', sort: 'asc' }];
    AgFns.initGrid(event ?? this.kiPugGridOptions, colDefs, sortModel);
  }

  @wrapBusy
  async onKiRowSelected(event: RowSelectedEvent) {
    if (!event.node.isSelected()) return;
    const ki = event.data as ProgramKitIssuance;
    if (ki == null) return;
    this.selectedKi = ki;
    if (ki.entityAspect.entityState.isDeleted()) {
      this.selectedKi = undefined;
    }

    this.refreshAvailableKiPugs(this.selectedKi);
  }

  refreshAvailableKiPugs(iss?: ProgramKitIssuance) {
    if (iss == null || iss.kitTag == null) {
      this.availableKiPugs = [];
    } else {
      const usedPugIds = new Set(
        _.flatten(
          this.program.programKitIssuances
            // all issuances with same tag as this one
            .filter((x) => x.kitTagId == iss.kitTagId)
            .map((x) => x.programKitIssuanceUserGroupMaps.map((x) => x.programUserGroupId)),
        ),
      );

      const pugs = this.program.approvalTree.approvalTreeUserGroups.map((x) => x.programUserGroup).filter((x) => x != null);
      const availablePugs = pugs.filter((x) => !usedPugIds.has(x.id));

      const currentPugs = iss.programKitIssuanceUserGroupMaps.map((x) => x.programUserGroup).filter((x) => x != null);
      availablePugs.push(...currentPugs);
      this.availableKiPugs = availablePugs;
    }
    AgFns.refreshGrid(this.kiPugGridOptions, this.availableKiPugs, 'sizeToFit');
    return this.availableKiPugs;
  }

  onKiPugCellValueChanged(event: CellValueChangedEvent) {
    if (this.selectedKi?.programKitIssuanceUserGroupMaps.length == 0) {
      this.toastr.warning('At least one Account User Group must be selected');
    }
  }

  onKiSelectAllGroups() {
    if (!this.selectedKi) return;
    for (const pug of this.availableKiPugs) {
      const exists = this.selectedKi.programKitIssuanceUserGroupMaps.find((x) => x.programUserGroupId === pug.id);
      if (!exists) {
        // Try to restore a deleted entity first, otherwise create a new one
        const restored = this.dbQueryService.uow.undoIfDeleted(ProgramKitIssuanceUserGroupMap, [this.selectedKi.id, pug.id]);
        if (!restored) {
          this.dbSaveService.createEntity(ProgramKitIssuanceUserGroupMap, {
            programKitIssuanceId: this.selectedKi.id,
            programUserGroupId: pug.id,
          });
        }
      }
    }
    this.kiPugGridOptions.apiX?.refreshCells();
  }

  onKiDeselectAllGroups() {
    if (!this.selectedKi) return;
    this.selectedKi.programKitIssuanceUserGroupMaps.slice().forEach((x) => EntityFns.deleteOrDetach(x.entityAspect));
    this.kiPugGridOptions.apiX?.refreshCells();
  }

  @wrapBusy
  async onKiAdd() {
    if (!this.kitIssuances) return;
    if (this.program.approvalTree == null) {
      await this.dialogService.okDialog('Unable to add', 'A Proximity Organization must be specified before you can add an issuance limit');
      return;
    }

    const ki = this.dbSaveService.createEntity(ProgramKitIssuance, {
      programId: this.program.id,
    });
    ki.entityAspect.validateEntity();

    this.kitIssuances?.push(ki);
    AgFns.refreshGrid(this.kiGridOptions, this.kitIssuances);
    AgFns.selectGridRowByKey(this.kiGridOptions, (e: ProgramKitIssuance) => e.id, ki.id, 'name');
  }

  @wrapBusy
  async onKiDelete(ki: ProgramKitIssuance) {
    if (!this.kitIssuances) return;

    UtilFns.assertNonNull(ki, 'Product Group should have been found');
    const ok = await this.dialogService.askIfOk(
      'Confirm Delete',
      `You will delete this Kit Issuance record.  THIS IS NOT REVERSIBLE.  Are you sure?`,
    );
    if (!ok) return;
    ki.programKitIssuanceUserGroupMaps.slice().forEach((x) => EntityFns.deleteOrDetach(x.entityAspect));
    EntityFns.deleteOrDetach(ki.entityAspect);

    // for display
    _.remove(this.kitIssuances, ki);
    AgFns.refreshGrid(this.kiGridOptions, this.kitIssuances);
    this.refreshAvailablePiPugs(undefined);
    //TODO: select 'next' row row
  }

  // --------------------------------------------------

  onAiGridReady(event?: GridReadyEvent) {
    const colDefs = [
      ProxAgFns.getEntityStateColDef(),

      { headerName: 'Name', field: 'name', editable: !this.parent.isReadOnly(), filter: 'agTextColumnFilter', ...AgFns.flexWidth(100) },
      {
        headerName: 'Program Product Tag',
        editable: !this.parent.isReadOnly(),
        filter: 'agTextColumnFilter',
        ...AgFns.createDropdownEditor('programProductTypeTagId', this.programProductTypeTags),
        colId: 'productTypeTag',
        ...AgFns.flexWidth(100),
      },

      { headerName: 'Max Qty', field: 'maximumQty', editable: !this.parent.isReadOnly(), ...AgFns.fixWidth(120) },
      { headerName: 'Timeframe Days', field: 'timeframeDays', editable: !this.parent.isReadOnly(), ...AgFns.fixWidth(160) },
      {
        ...AgFns.createIncludedInListCheckBoxProps(
          ProgramAccountIssuanceMap,
          this.dbQueryService.uow,
          'Is Included?',
          (e) => {
            return e.data.programAccountIssuanceMaps.find((x) => x.programId == this.program.id) != null;
          },
          (e) => {
            return {
              programId: this.program.id,
              accountIssuanceId: e.data.id,
            };
          },
          !this.parent.isReadOnly(),
        ),
        ...AgFns.fixWidth(120),
      },
      this.parent.ifEditable(ProxAgFns.getEntityDeleteColDef(this.onAiDelete.bind(this))),
    ];
    const sortModel: ISortModel = [{ colId: 'name', sort: 'asc' }];
    AgFns.initGrid(event ?? this.aiGridOptions, colDefs, sortModel);
  }

  onAiPugGridReady(event?: GridReadyEvent) {
    const colDefs = [
      {
        headerName: 'Applies to Group',
        field: 'name',
        cellRenderer: 'agGroupCellRenderer',
        ...AgFns.flexWidth(100),
      },
      {
        ...AgFns.createIncludedInListCheckBoxProps(
          AccountIssuanceUserGroupMap,
          this.dbQueryService.uow,
          'Is Included?',
          (e) => {
            return this.selectedAi?.accountIssuanceUserGroupMaps.find((x) => x.programUserGroupId == e.data.id) != null;
          },
          (e) => {
            return {
              accountIssuanceId: this.selectedAi?.id,
              programUserGroupId: e.data.id,
            };
          },
          !this.parent.isReadOnly(),
        ),
        ...AgFns.fixWidth(120),
      },
    ];
    const sortModel: ISortModel = [{ colId: 'name', sort: 'asc' }];
    AgFns.initGrid(event ?? this.aiPugGridOptions, colDefs, sortModel);
  }

  @wrapBusy
  async onAiRowSelected(event: RowSelectedEvent) {
    if (!event.node.isSelected()) return;
    const iss = event.data as AccountIssuance;
    if (iss == null) return;
    this.selectedAi = iss;
    if (iss.entityAspect.entityState.isDeleted()) {
      this.selectedAi = undefined;
    }

    this.refreshAvailableAiPugs(this.selectedAi);
    await this.refreshAiTagProducts();
  }

  @wrapBusy
  async onAiAdd() {
    if (!this.accountIssuances) return;

    const iss = this.dbSaveService.createEntity(AccountIssuance, {
      accountId: this.program.accountId,
    });
    iss.entityAspect.validateEntity();

    this.accountIssuances?.push(iss);
    AgFns.refreshGrid(this.aiGridOptions, this.accountIssuances);
    AgFns.selectGridRowByKey(this.aiGridOptions, (e: AccountIssuance) => e.id, iss.id, 'name');
  }

  @wrapBusy
  async onAiDelete(ai: AccountIssuance) {
    if (!this.accountIssuances) return;

    UtilFns.assertNonNull(ai, 'Product Group should have been found');
    const ok = await this.dialogService.askIfOk(
      'Confirm Delete',
      `This will delete this issuance along with all job order logs associated with it. Are you sure?`,
    );
    if (!ok) return;
    const logs = await this.dbQueryService.getAccountIssuanceUserLogsByAccountIssuanceId(ai.id);
    logs.forEach((x) => EntityFns.deleteOrDetach(x.entityAspect));

    ai.programAccountIssuanceMaps.slice().forEach((x) => EntityFns.deleteOrDetach(x.entityAspect));
    ai.accountIssuanceUserGroupMaps.slice().forEach((x) => EntityFns.deleteOrDetach(x.entityAspect));
    EntityFns.deleteOrDetach(ai.entityAspect);

    // for display
    _.remove(this.accountIssuances, ai);
    AgFns.refreshGrid(this.aiGridOptions, this.accountIssuances);
    this.refreshAvailableAiPugs(undefined);
    //TODO: select 'next' row row
  }

  onAiCellValueChanged(event: CellValueChangedEvent) {
    if (event.colDef.colId == 'productTypeTag') {
      this.selectedAi?.accountIssuanceUserGroupMaps.slice().forEach((x) => EntityFns.deleteOrDetach(x.entityAspect));
      this.refreshAvailableAiPugs(this.selectedAi);
      this.refreshAiTagProducts();
    }
  }

  onAiAugCellValueChanged(event: CellValueChangedEvent) {
    if (this.selectedAi?.accountIssuanceUserGroupMaps.length == 0) {
      this.toastr.warning('At least one user group must be selected.');
    }
  }

  onAiSelectAllGroups() {
    if (!this.selectedAi) return;
    for (const pug of this.availableAiPugs) {
      const exists = this.selectedAi.accountIssuanceUserGroupMaps.find((x) => x.programUserGroupId === pug.id);
      if (!exists) {
        // Try to restore a deleted entity first, otherwise create a new one
        const restored = this.dbQueryService.uow.undoIfDeleted(AccountIssuanceUserGroupMap, [this.selectedAi.id, pug.id]);
        if (!restored) {
          this.dbSaveService.createEntity(AccountIssuanceUserGroupMap, {
            accountIssuanceId: this.selectedAi.id,
            programUserGroupId: pug.id,
          });
        }
      }
    }
    this.aiPugGridOptions.apiX?.refreshCells();
  }

  onAiDeselectAllGroups() {
    if (!this.selectedAi) return;
    this.selectedAi.accountIssuanceUserGroupMaps.slice().forEach((x) => EntityFns.deleteOrDetach(x.entityAspect));
    this.aiPugGridOptions.apiX?.refreshCells();
  }

  // The grid on this tab is supposed to be per-program, not per-account. The data model
  // has no direct "AI belongs to program" flag, so we derive scope from PUGs: an AI is in
  // scope for this program iff it attaches to at least one PUG that lives in this
  // program's approval tree. (The bracketed "[Program Name]" suffix admins put in AI
  // names is just a convention — it isn't a structural signal.)
  //
  // Also kept visible:
  //   - AIs in Added state (just created via "+ Add Account Issuance Limit", no PUGs yet)
  //   - AIs already mapped to this program via ProgramAccountIssuanceMap in any non-detached
  //     state (defensive: covers the unlikely case of an included AI whose PUGs were all
  //     removed but inclusion remained)
  private filterAccountIssuancesForProgram(allAis: AccountIssuance[]): AccountIssuance[] {
    const programPugIds = new Set<string>((this.program.approvalTree?.approvalTreeUserGroups ?? []).map((x) => x.programUserGroupId));

    return allAis.filter((ai) => {
      if (ai.entityAspect.entityState.isAdded()) return true;

      const hasPugInThisProgram = ai.accountIssuanceUserGroupMaps.some(
        (m) => !m.entityAspect.entityState.isDetached() && programPugIds.has(m.programUserGroupId),
      );
      if (hasPugInThisProgram) return true;

      return ai.programAccountIssuanceMaps.some((m) => m.programId === this.program.id && !m.entityAspect.entityState.isDetached());
    });
  }

  refreshAvailableAiPugs(iss?: AccountIssuance) {
    if (iss == null || iss.programProductTypeTag == null) {
      this.availableAiPugs = [];
    } else {
      const usedPugIds = new Set(
        _.flatten(
          this.program.account.accountIssuances
            .filter((x) => x.programProductTypeTagId == iss.programProductTypeTagId)
            .map((x) => x.accountIssuanceUserGroupMaps.map((y) => y.programUserGroupId)),
        ),
      );

      if (this.program.approvalTree && this.program.approvalTree.approvalTreeUserGroups && iss.accountIssuanceUserGroupMaps) {
        const pugs = this.program.approvalTree.approvalTreeUserGroups.map((x) => x.programUserGroup).filter((x) => x != null);
        const availablePugs = pugs.filter((x) => !usedPugIds.has(x.id));
        const currentPugs = iss.accountIssuanceUserGroupMaps.map((x) => x.programUserGroup).filter((x) => x != null);
        availablePugs.push(...currentPugs);
        this.availableAiPugs = availablePugs;
      }
    }
    AgFns.refreshGrid(this.aiPugGridOptions, this.availableAiPugs, 'sizeToFit');
    return this.availableAiPugs;
  }

  onAiTagProductGridReady(event?: GridReadyEvent) {
    const ptypePath = 'productType';
    const colDefs = [
      { ...ProxAgFns.getSupplierColDef(ptypePath + '.supplier.name') },
      { ...ProxAgFns.getManufacturerColDef(ptypePath + '.manufacturer.name') },
      { ...ProxAgFns.getProductTypeNameColDef(ptypePath + '.name') },
    ];
    const sortModel: ISortModel = [
      { colId: ptypePath + '.supplier.name', sort: 'asc' },
      { colId: ptypePath + '.manufacturer.name', sort: 'asc' },
      { colId: ptypePath + '.name', sort: 'asc' },
    ];
    AgFns.initGrid(event ?? this.aiTagProductGridOptions, colDefs, sortModel);
  }

  async refreshAiTagProducts() {
    if (this.selectedAi == null || this.selectedAi.programProductTypeTag == null) {
      this.aiTagProducts = [];
    } else {
      this.aiTagProducts = await this.dbQueryService.getPricedProductTypesForTag(this.selectedAi.programProductTypeTagId);
    }
    await AgFns.refreshGrid(this.aiTagProductGridOptions, this.aiTagProducts, 'sizeToFit');
  }

  // -------------------------------------------------------------------------

  // TODO: handle accountIssuances
  async markError(errEnt: Entity, propName?: string) {
    const ent = errEnt as ProgramIssuance;
    AgFns.selectGridRowByKey(this.piGridOptions, (e: ProgramIssuance) => e.id, ent.id, propName);
  }
}
